import type { Type } from '@angular/core';
import type { Route, Routes } from '@angular/router';
import { APP_PATHS, SEO_SCHEMA_TYPES } from '@dev-fast/types';

import { IsAuthGuard } from '@app/auth';
import {
  AutoGameActivateChildGuard,
  AutoGameDeactivateGuard,
  BanActivateGuard,
  bettingSurfaceGuard,
  caseDomainOnlyGuard,
  casesOnlyGuard,
  geoBlockGuard,
} from '@app/core/guards';
import { SeoType } from '@app/core/seo-service';

import { environment } from '../../environments/environment';
import { surfaceAuthWallCanMatchGuard } from '../guards/surface-auth-wall.guard';

const isStoreBuild = Boolean(environment.WELCOME_LAYOUT);
const isCasesOnlyBuild = Boolean(environment.CASES_ONLY_MODE);

const loadResponsiblePlayPolicyComponent = (): Promise<Type<unknown>> => {
  if (isCasesOnlyBuild) {
    return import('@app/modules/cases-legal').then(({ ResponsiblePlayPolicyComponent }) => ResponsiblePlayPolicyComponent);
  }

  return import('@app/modules/responsible-play-policy').then(({ ResponsiblePlayPolicyComponent }) => ResponsiblePlayPolicyComponent);
};

const withSurfaceAuthWall = (route: Route): Route => ({
  ...route,
  canMatch: [surfaceAuthWallCanMatchGuard, ...(route.canMatch ?? [])],
});

export const footerRoutes: Routes = [
  {
    path: 'tos',
    data: { seo: 'TAC', seoType: [SeoType.TITLE, SeoType.DESCRIPTION], noAlternative: true },
    loadComponent: () => import('@app/modules/tos').then(({ TosComponent }) => TosComponent),
  },
  {
    path: APP_PATHS.privacyPolicy,
    data: { seo: 'PRIVACY_POLICY', seoType: [SeoType.TITLE, SeoType.DESCRIPTION], noAlternative: true },
    loadComponent: () => import('@app/modules/privacy-policy').then(({ PrivacyPolicyComponent }) => PrivacyPolicyComponent),
  },
  withSurfaceAuthWall({
    path: APP_PATHS.antiBriberyPolicy,
    data: { seo: 'ANTI_BRIBERY_POLICY', seoType: [SeoType.TITLE, SeoType.DESCRIPTION], noAlternative: true },
    loadComponent: () => import('@app/modules/anti-bribery-policy').then(({ AntiBriberyPolicyComponent }) => AntiBriberyPolicyComponent),
    canActivate: [casesOnlyGuard],
  }),
  withSurfaceAuthWall({
    path: APP_PATHS.amlPolicy,
    data: { seo: 'AML_POLICY', seoType: [SeoType.TITLE, SeoType.DESCRIPTION], noAlternative: true },
    loadComponent: () => import('@app/modules/aml-policy').then(({ AmlPolicyComponent }) => AmlPolicyComponent),
    canActivate: [casesOnlyGuard],
  }),
  withSurfaceAuthWall({
    path: APP_PATHS.refundPolicy,
    data: { seo: 'REFUND_POLICY', seoType: [SeoType.TITLE, SeoType.DESCRIPTION], noAlternative: true },
    loadComponent: () => import('@app/modules/refund-policy').then(({ RefundPolicyComponent }) => RefundPolicyComponent),
  }),
  withSurfaceAuthWall({
    path: APP_PATHS.cookiePolicy,
    data: { seo: 'COOKIE_POLICY', seoType: [SeoType.TITLE, SeoType.DESCRIPTION], noAlternative: true },
    loadComponent: () => import('@app/modules/cases-legal').then(({ CookiePolicyComponent }) => CookiePolicyComponent),
    canMatch: [caseDomainOnlyGuard],
  }),
  ...(isStoreBuild
    ? []
    : [
        withSurfaceAuthWall({
          path: APP_PATHS.responsiblePlayPolicy,
          data: {
            seo: 'RESPONSIBLE_PLAY_POLICY',
            seoType: [SeoType.H1, SeoType.TITLE, SeoType.DESCRIPTION],
            noAlternative: true,
          },
          loadComponent: loadResponsiblePlayPolicyComponent,
        }),
      ]),
  withSurfaceAuthWall({
    path: APP_PATHS.bettingRules,
    data: { seo: 'BETTING_RULES', seoType: [SeoType.TITLE, SeoType.DESCRIPTION], noAlternative: true, redirectTo: '/login' },
    loadComponent: () => import('@app/modules/betting-rules').then(({ BettingRulesComponent }) => BettingRulesComponent),
    // Same availability as the sportsbook itself: the surface guard cuts cases/fastcs2 and profiles
    // with betting disabled, geoBlockGuard cuts restricted countries, and IsAuthGuard keeps the page
    // auth-only exactly like /betting (guest → /login with `redirectToAfterLogin`, auth runs LAST so a
    // guest on a betting-less/geo-blocked surface is not bounced into the login flow). The page is
    // therefore out of every seo-routes list — no prerender, no sitemap entry. On store builds the MAIN
    // route tree sits behind WelcomeWorkDeactivateGuard, and the guest-facing welcome tree gets
    // `welcomeFooterRoutes` below, which strips this route — the public pre-auth store surface must
    // never expose sportsbook legal (ads moderation).
    canActivate: [bettingSurfaceGuard, geoBlockGuard, IsAuthGuard],
  }),
  withSurfaceAuthWall({
    path: APP_PATHS.fairPlayPolicy,
    data: { seo: 'FAIR_PLAY_POLICY', seoType: [SeoType.TITLE, SeoType.DESCRIPTION], noAlternative: true },
    loadComponent: () => import('@app/modules/cases-legal').then(({ FairPlayPolicyComponent }) => FairPlayPolicyComponent),
    canMatch: [caseDomainOnlyGuard],
  }),
  withSurfaceAuthWall({
    path: APP_PATHS.creditsPolicy,
    data: { seo: 'CREDITS_POLICY', seoType: [SeoType.TITLE, SeoType.DESCRIPTION], noAlternative: true },
    loadComponent: () => import('@app/modules/cases-legal').then(({ CreditsPolicyComponent }) => CreditsPolicyComponent),
    canMatch: [caseDomainOnlyGuard],
  }),
  withSurfaceAuthWall({
    path: APP_PATHS.itemsUsePolicy,
    data: { seo: 'ITEMS_USE_POLICY', seoType: [SeoType.TITLE, SeoType.DESCRIPTION], noAlternative: true },
    loadComponent: () => import('@app/modules/cases-legal').then(({ ItemsUsePolicyComponent }) => ItemsUsePolicyComponent),
    canMatch: [caseDomainOnlyGuard],
  }),
  withSurfaceAuthWall({
    path: APP_PATHS.provablyFair,
    loadChildren: () => import('@app/modules/provably-fair').then(({ routes: provablyFairRoutes }) => provablyFairRoutes),
    data: {
      seo: 'PROVABLY_FAIR',
      seoType: [SeoType.TITLE, SeoType.DESCRIPTION],
      requiredFeatureFlags: ['provably-fair'],
    },
    canDeactivate: [AutoGameDeactivateGuard],
    canActivate: [casesOnlyGuard, BanActivateGuard],
    canActivateChild: [AutoGameActivateChildGuard],
  }),
  withSurfaceAuthWall({
    path: 'reviews',
    data: { seo: 'REVIEWS', seoType: [SeoType.TITLE, SeoType.DESCRIPTION], schemaType: SEO_SCHEMA_TYPES.LEGACY },
    loadComponent: () => import('@app/modules/reviews').then(({ ReviewsComponent }) => ReviewsComponent),
  }),
  withSurfaceAuthWall({
    path: 'bug-bounty',
    data: { seo: 'BUGBOUNTY', seoType: [SeoType.TITLE, SeoType.DESCRIPTION] },
    loadComponent: () => import('@app/modules/bug-bounty').then(({ BugBountyComponent }) => BugBountyComponent),
  }),
  withSurfaceAuthWall({
    path: 'about',
    data: {
      seo: 'ABOUT',
      seoType: [SeoType.TITLE, SeoType.DESCRIPTION],
      schemaType: SEO_SCHEMA_TYPES.LEGACY,
    },
    loadComponent: () => import('@app/modules/about').then(({ AboutComponent }) => AboutComponent),
  }),
];

/**
 * Footer routes for the GUEST-facing welcome tree (store landing). Betting legal is stripped: on
 * store the sportsbook is auth-only (the main tree sits behind WelcomeWorkDeactivateGuard), and the
 * public pre-auth surface must stay free of sportsbook mentions for ads moderation.
 */
export const welcomeFooterRoutes: Routes = footerRoutes.filter((route) => route.path !== APP_PATHS.bettingRules);
